import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import logo from '@/assets/logo.png';
import { motion, AnimatePresence } from "framer-motion";
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Close mobile menu when route changes
    setIsOpen(false);
  }, [location]);
  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => {
    setIsOpen(false);
  };

  // Define menu items - Simple navigation without dropdowns
  const menuItems = [{
    label: 'ראשי',
    path: '/'
  }, {
    label: 'הוצאה לפועל',
    path: '/enforcement'
  }, {
    label: 'חדלות פירעון',
    path: '/insolvency'
  }, {
    label: 'מקרקעין',
    path: '/real-estate'
  }, {
    label: 'אודות',
    path: '/about'
  }, {
    label: 'צור קשר',
    path: '/contact'
  }, {
    label: 'מחשבון זכאות חדלות פירעון',
    path: 'https://mahshevon.lovable.app/',
    isExternal: true,
    isNew: true
  }, {
    label: 'עוזר משפטי AI',
    path: '/legal-assistant',
    isNew: true
  }, {
    label: 'מחשבון צו תשלומים',
    path: 'https://szlaw.lovable.app/',
    isExternal: true,
    isNew: true
  }];
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Mobile menu animations
  const mobileMenuVariants = {
    closed: {
      x: "100%",
      opacity: 0
    },
    open: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: {
      x: "100%",
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };
  const dropdownVariants = {
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    }
  };
  const staggerChildren = {
    open: {
      transition: {
        staggerChildren: 0.07
      }
    },
    closed: {
      transition: {
        staggerChildren: 0.03,
        staggerDirection: -1
      }
    }
  };
  const menuItemVariants = {
    closed: {
      y: 20,
      opacity: 0
    },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        ease: "easeOut",
        duration: 0.3
      }
    }
  };
  return <header className={cn("fixed w-full top-0 z-50 transition-all duration-300 flex flex-col", isScrolled ? "bg-white shadow-md" : "bg-transparent")}>
      <div className="container mx-auto px-4 md:px-6 pt-3 md:pt-4">
        <div className="flex items-center justify-center">
          <Link to="/" className="flex items-center justify-center" onClick={closeMenu}>
            <img src={logo} alt="Smirnov Zooler Law Firm - סמירנוב צולר משרד עורכי דין" className="h-12 w-auto mx-auto" />
          </Link>

          {/* Mobile menu button */}
          <Button variant="ghost" size="icon" className="md:hidden absolute right-4 top-4" onClick={toggleMenu} aria-label={isOpen ? "סגור תפריט" : "פתח תפריט"}>
            <AnimatePresence mode="wait">
              {isOpen ? <motion.div key="close" initial={{
              opacity: 0,
              rotate: -90
            }} animate={{
              opacity: 1,
              rotate: 0
            }} exit={{
              opacity: 0,
              rotate: 90
            }} transition={{
              duration: 0.2
            }}>
                  <X size={24} />
                </motion.div> : <motion.div key="menu" initial={{
              opacity: 0,
              rotate: 90
            }} animate={{
              opacity: 1,
              rotate: 0
            }} exit={{
              opacity: 0,
              rotate: -90
            }} transition={{
              duration: 0.2
            }}>
                  <Menu size={24} />
                </motion.div>}
            </AnimatePresence>
          </Button>
        </div>

        {/* Desktop menu - Simple navigation links */}
        <nav className="hidden md:flex justify-center py-4">
          <div className="flex items-center gap-8">
            {menuItems.map(item => {
            const linkContent = <span className="flex items-center gap-2">
                  {item.isNew && <span className="text-green-600 text-xs font-semibold">
                      חדש
                    </span>}
                  {item.label}
                </span>;
            if (item.isExternal) {
              return <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-colors hover:text-foreground font-normal text-muted-foreground" onClick={closeMenu}>
                    {linkContent}
                  </a>;
            }
            return <Link key={item.path} to={item.path} className={cn("px-3 py-2 text-sm transition-colors hover:text-foreground", isActive(item.path) ? "font-medium text-foreground" : "font-normal text-muted-foreground")} onClick={closeMenu}>
                  {linkContent}
                </Link>;
          })}
          </div>
        </nav>
      </div>

      {/* Mobile menu - Using AnimatePresence for smooth animations */}
      <AnimatePresence>
        {isOpen && isMobile && <motion.div initial="closed" animate="open" exit="exit" variants={mobileMenuVariants} className="fixed inset-0 bg-white z-40 pt-20 md:hidden overflow-y-auto">
            <div className="absolute left-4 top-4 rtl:left-auto rtl:right-4">
              <Button variant="ghost" size="icon" onClick={closeMenu} aria-label="חזרה" className="backdrop-blur-sm bg-white/80 rounded-full mx-[300px]">
                <ArrowLeft size={20} />
              </Button>
            </div>
            
            <motion.nav className="container px-4 py-4" variants={staggerChildren}>
              {menuItems.map(item => {
            const linkContent = <span className="w-full text-right text-lg font-medium flex items-center gap-2">
                    {item.isNew && <span className="text-green-600 text-sm font-semibold">
                        חדש
                      </span>}
                    {item.label}
                  </span>;
            return <motion.div key={item.path} className="mb-6" variants={menuItemVariants}>
                    <div className={cn("flex justify-between items-center py-3 border-b", !item.isExternal && isActive(item.path) ? "border-primary" : "border-gray-200")}>
                      {item.isExternal ? <a href={item.path} target="_blank" rel="noopener noreferrer" className="w-full text-gray-800" onClick={closeMenu}>
                          {linkContent}
                        </a> : <Link to={item.path} className={cn("w-full", isActive(item.path) ? "text-primary" : "text-gray-800")} onClick={closeMenu}>
                          {linkContent}
                        </Link>}
                    </div>
                  </motion.div>;
          })}
            </motion.nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
};
export default Header;